import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorLoad, setErrorLoad] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", organization: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    api.get(`/invitations/${token}`)
      .then((r) => {
        if (cancel) return;
        setInvite(r.data);
        setForm((f) => ({
          ...f,
          email: r.data.email || "",
          organization: r.data.organization || "",
        }));
      })
      .catch((e) => {
        if (cancel) return;
        setErrorLoad(formatApiErrorDetail(e.response?.data?.detail) || "Invalid or expired link");
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register-with-invite", { token, ...form });
      toast.success(`Welcome to EuroFind, ${data.name}.`);
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not complete registration");
    }
    setSubmitting(false);
  };

  if (loading) {
    return <div className="mx-auto max-w-md px-6 py-20 font-body text-sm text-[#5C6873]" data-testid="invite-loading">Verifying invitation…</div>;
  }

  if (errorLoad) {
    return (
      <div className="mx-auto max-w-md px-6 py-20" data-testid="invite-invalid">
        <div className="rounded-lg border border-[#9E4747]/30 bg-[#9E4747]/5 p-8">
          <AlertTriangle className="text-[#9E4747]" />
          <h1 className="mt-3 font-heading text-2xl font-semibold text-[#1A2024]">Invitation unavailable</h1>
          <p className="mt-2 font-body text-sm text-[#5C6873]">{errorLoad}</p>
          <Link to="/" className="mt-4 inline-block font-body text-sm font-medium text-[#2E3A46] hover:underline">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-6 py-16" data-testid="invite-page">
      <Badge variant="outline" className="rounded-full border-[#4A7C59]/30 bg-[#4A7C59]/10 font-body text-[11px] text-[#4A7C59]">
        <ShieldCheck size={12} className="mr-1" /> Pre-approved authority invitation
      </Badge>
      <h1 className="mt-3 font-heading text-3xl font-semibold text-[#1A2024]">Set up your authority account</h1>
      <p className="mt-2 font-body text-sm text-[#5C6873]">
        Your account will be activated automatically upon registration.
        {invite.note && <span className="mt-2 block italic">Note: “{invite.note}”</span>}
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <Label className="font-body text-sm text-[#1A2024]">Full name</Label>
          <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 h-11" data-testid="invite-name-input" />
        </div>
        <div>
          <Label className="font-body text-sm text-[#1A2024]">Email</Label>
          <Input type="email" required value={form.email}
            disabled={!!invite.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="mt-1.5 h-11 disabled:bg-[#F8F9FA] disabled:text-[#5C6873]"
            data-testid="invite-email-input" />
          {invite.email && <p className="mt-1 font-body text-xs text-[#5C6873]">This invitation is locked to this email.</p>}
        </div>
        <div>
          <Label className="font-body text-sm text-[#1A2024]">Organisation</Label>
          <Input value={form.organization}
            onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
            className="mt-1.5 h-11"
            data-testid="invite-organization-input" />
        </div>
        <div>
          <Label className="font-body text-sm text-[#1A2024]">Password</Label>
          <Input type="password" required minLength={6} value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className="mt-1.5 h-11" data-testid="invite-password-input" />
          <p className="mt-1 font-body text-xs text-[#5C6873]">At least 6 characters.</p>
        </div>

        {error && <div className="rounded-md border border-[#9E4747]/30 bg-[#9E4747]/5 px-3 py-2 font-body text-sm text-[#9E4747]" data-testid="invite-error">{error}</div>}

        <Button type="submit" disabled={submitting} className="h-11 w-full bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="invite-submit-btn">
          {submitting ? "Creating account…" : "Activate authority account"}
        </Button>
      </form>
    </div>
  );
}
